import { useEffect } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';

const panels = [
  {
    id: 'or',
    label: 'Or',
    price: 'Inkludert',
    priceNote: 'Standard',
    img: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/2af13cd97_Zrzutekranu2026-04-8o235241.png',
    description: 'Klassisk or-panel med lys, jevn tekstur. Avgir lite harpiks, tåler varme godt og gir en rolig, tradisjonell badstuatmosfære.',
    pros: ['Lys og jevn overflate', 'Lite harpiks', 'Tradisjonelt nordisk uttrykk', 'God varmetoleranse'],
    color: 'Lys beige/gulaktig',
    colorHex: '#e8d5b0',
    colorDesc: 'Lys, varm beige med svake gule undertoner. Lyst og naturlig utseende som gir en åpen, luftig badstuopplevelse.',
    feel: 'Varm og klassisk',
  },
  {
    id: 'abachi',
    label: 'Abachi',
    price: '+5 000 kr',
    priceNote: 'Tillegg',
    img: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/9393d8a16_Abachi.png',
    description: 'Afrikansk treslag med svært lav varmeledningsevne, perfekt rundt ovn og benker. Glatt overflate og lys farge gir et premium-preg.',
    pros: ['Svært lav varmeledningsevne', 'Perfekt ved ovn og benker', 'Glatt og behagelig', 'Lys, eksklusiv farge'],
    color: 'Kremhvit/lys',
    colorHex: '#f0e8d5',
    colorDesc: 'Nesten hvit kremfarge, lysere enn or. Svært jevn og ren overflate som gir et moderne, minimalistisk preg.',
    feel: 'Eksklusiv og lys',
  },
  {
    id: 'termo-asp',
    label: 'Termo Asp',
    price: '+8 000 kr',
    priceNote: 'Tillegg',
    img: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/3f9e75e83_Thermo.png',
    description: 'Varmebehandlet asp med forbedret stabilitet og fuktmotstand. Mørkere tone enn vanlig asp, med et eksklusivt og moderne utseende.',
    pros: ['Forbedret stabilitet', 'God fuktmotstand', 'Mørkere eksklusiv tone', 'Moderne utseende'],
    color: 'Mellombrun/honning',
    colorHex: '#b5813a',
    colorDesc: 'Varm honningbrun farge med gylne skjær. Tydelig mørkere enn or og abachi — gir en eksklusiv, moderne atmosfære.',
    feel: 'Moderne og varm',
  },
  {
    id: 'sort-asp-sts4',
    label: 'Sort Asp STS4 IGNITE',
    price: '+15 000 kr',
    priceNote: 'Luksus',
    img: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/9193d7b00_ThermoOr.jpg',
    description: 'Termobehandlet osika med STS4-sertifisering og eksklusiv IGNITE sort finish. Intens mørk fargetone som er den mest intensive blant alle materialer. Gir et eksklusivt og minimalistisk uttrykk med silkemyk overflate og høyeste holdbarhetsnivå.',
    pros: ['STS4, høyeste sertifiseringsklasse', 'Eksklusiv IGNITE sort finish', 'Ekstremt dimensjonsstabilt', 'Intens mørk og minimalistisk'],
    color: 'Intensiv jet-svart',
    colorHex: '#1a1a1a',
    colorDesc: 'Intens jet-svart farge — den mest intensive og mørke tonen av alle panelalternativene. Gir et eksklusivt, moderni og kunstnerisk uttrykk.',
    feel: 'Dramatisk og eksklusiv',
  },
  {
    id: 'termo-radiata',
    label: 'Termo Radiata STS4',
    price: '+18 000 kr',
    priceNote: 'Luksus',
    img: 'https://media.base44.com/images/public/69db395aa041ab4d87ab84d7/505f74ede_TermoRadiata.png',
    description: 'Varmebehandlet radiata-furu med STS4-sertifisering, den høyeste klassen innen termisk behandlet tre. Ekstremt dimensjonsstabilt, motstandsdyktig mot fukt og gir en dyp, rik brunton med silkemyk overflate.',
    pros: ['STS4, høyeste sertifiseringsklasse', 'Ekstremt dimensjonsstabilt', 'Svært god fuktmotstand', 'Dyp rik brunton med silkemyk finish'],
    color: 'Dyp rødbrun/mahogny',
    colorHex: '#4a1e0e',
    colorDesc: 'Mørk mahognyrød farge — den dypeste og mest intensive tonen av alle panelalternativene. Gir et dramatisk, eksklusivt uttrykk nær malt tre.',
    feel: 'Eksklusiv og robust',
  },
];

export default function InnvendigPanel() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}
        className="relative h-[500px] overflow-hidden">
        <img src="https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/9193d7b00_ThermoOr.jpg"
          alt="Innvendige Panel" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 to-black/50"></div>
        <div className="absolute inset-0 flex flex-col justify-end p-12 z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="text-sm tracking-widest font-body uppercase text-white/60 mb-4">Innvendig materialer · Artikkel 1</p>
            <h1 className="font-heading text-6xl md:text-7xl font-light leading-[1.0] text-white mb-4">Innvendige Panel</h1>
            <p className="font-body text-white/80 text-xl max-w-2xl leading-relaxed">
              Velg det panelet som passer din stil — fra klassisk or til eksklusiv termo or
            </p>
          </motion.div>
        </div>
      </motion.div>

      <div className="max-w-6xl mx-auto px-6 md:px-12 py-20">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mb-16 max-w-3xl">
          <h2 className="font-heading text-4xl font-light text-[#2a1810] mb-6">Hvilke paneler kan du velge?</h2>
          <p className="font-body text-lg text-foreground/70 leading-relaxed">
            Her er en kortfattet oversikt over panelalternativene du kan velge mellom. Hvert treslag har sine egne egenskaper og estetikk, tilpasset ulike behov og preferanser.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 mb-20">
          {panels.map((panel, idx) => (
            <motion.div key={panel.id}
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: idx * 0.1 }}
              whileHover={{ y: -6 }}
              className="bg-white border border-border/20 rounded-[12px] overflow-hidden hover:shadow-xl hover:border-[#2d5a3d] transition-all">
              <div className="relative h-80 overflow-hidden">
                <img src={panel.img} alt={panel.label} className="w-full h-full object-cover" />
                <div className="absolute top-4 right-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-body font-medium ${panel.priceNote === 'Standard' ? 'bg-[#2d5a3d] text-white' : panel.priceNote === 'Premium' ? 'bg-[#2a1810] text-white' : 'bg-white/90 text-[#2a1810]'}`}>
                    {panel.price}
                  </span>
                </div>
              </div>
              <div className="p-8">
                <h3 className="font-heading text-2xl font-light text-[#2a1810] mb-3">{panel.label}</h3>
                <p className="font-body text-base text-foreground/70 leading-relaxed mb-4">{panel.description}</p>
                <div className="flex items-center gap-3 mb-4 p-3 bg-secondary/20 rounded-[8px]">
                  <div className="w-8 h-8 rounded-full flex-shrink-0 border border-border/30" style={{ backgroundColor: panel.colorHex }}></div>
                  <div>
                    <p className="font-body text-xs text-foreground/50 uppercase tracking-widest">{panel.color}</p>
                    <p className="font-body text-base text-foreground/60 leading-relaxed">{panel.colorDesc}</p>
                  </div>
                </div>
                <div className="border-t border-border/20 pt-4">
                  <p className="font-body text-xs uppercase tracking-widest text-foreground/40 mb-3">Fordeler</p>
                  <div className="grid grid-cols-2 gap-2">
                    {panel.pros.map((pro, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Check size={12} className="text-[#2d5a3d] flex-shrink-0" />
                        <span className="font-body text-base text-foreground/60">{pro}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="bg-gradient-to-br from-[#2a1810]/5 to-[#2d5a3d]/5 border border-border/20 rounded-[12px] p-10 mb-12">
          <h3 className="font-heading text-2xl font-light text-[#2a1810] mb-4">Hvilken panel bør du velge?</h3>
          <div className="grid md:grid-cols-3 gap-6 font-body text-sm text-foreground/70">
            <div>
              <p className="font-semibold text-[#2a1810] mb-2">Tradisjonell og budsjettvennlig</p>
              <p>Or og Abachi er standardvalgene, klassisk nordisk utseende, godt med varme og ingen ekstra kostnad.</p>
            </div>
            <div>
              <p className="font-semibold text-[#2a1810] mb-2">Moderne og eksklusiv</p>
              <p>Termo Asp gir et mørkere, mer tidløst preg og forbedret fuktmotstand.</p>
            </div>
            <div>
              <p className="font-semibold text-[#2a1810] mb-2">Luksus og holdbarhet</p>
              <p>Termo Radiata STS4 og Sort Asp STS4 IGNITE — toppnivå luksus med silkeglatt finish og ekstremt holdbarhet.</p>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-between">
          <a href="/laer-mer-handverk" className="inline-flex items-center gap-2 px-6 py-3 border border-border text-foreground font-body text-sm tracking-widest hover:bg-foreground hover:text-background transition-all rounded-[4px]">
            <ArrowLeft size={16} /> Tilbake til Lær Mer
          </a>
          <a href="/innvendig-gulv" className="inline-flex items-center gap-2 px-8 py-4 bg-[#2a1810] text-white font-body tracking-widest text-sm font-semibold rounded-[4px] hover:bg-[#2a1810]/90 transition-all">
            Neste: Gulv <ArrowRight size={16} />
          </a>
        </div>
      </div>
      <Footer />
    </div>
  );
}